import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { CartProvider } from '../contexts/CartContext';
import { useLanguage } from '../contexts/LanguageContext';
import { QRMenu } from '../pages/QRMenu';
import { QRCategory } from '../pages/QRCategory';
import { QRItem } from '../pages/QRItem';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Parse the wildcard segments after `/:slug/` and return { tableNumber, page, entityId }.
 *
 * Supported patterns:
 *   ""                                → { page: 'menu' }
 *   "masa3"                           → { tableNumber: '3', page: 'menu' }
 *   "category/abc"                    → { page: 'category', entityId: 'abc' }
 *   "masa3/category/abc"              → { tableNumber: '3', page: 'category', entityId: 'abc' }
 *   "item/abc"                        → { page: 'item', entityId: 'abc' }
 *   "masa3/item/abc"                  → { tableNumber: '3', page: 'item', entityId: 'abc' }
 *   "cart"                            → { page: 'cart' }
 *   anything else                     → { page: null } (invalid → redirect home)
 */
function parseSlugPath(rest) {
  const parts = (rest || '').split('/').filter(Boolean);
  const result = { tableNumber: null, page: 'menu', entityId: null };
  let i = 0;

  // Optional first segment: "masaN" or legacy "tN"
  if (parts[i]) {
    const m = String(parts[i]).toLowerCase().match(/^(?:masa|t)(\d+)$/);
    if (m) {
      result.tableNumber = m[1];
      i += 1;
    }
  }

  if (i >= parts.length) return result;

  const nextSegment = parts[i].toLowerCase();
  if (nextSegment === 'category' && parts[i + 1]) {
    result.page = 'category';
    result.entityId = parts[i + 1];
    return result;
  }
  if (nextSegment === 'item' && parts[i + 1]) {
    result.page = 'item';
    result.entityId = parts[i + 1];
    return result;
  }
  if (nextSegment === 'cart') {
    result.page = 'cart';
    return result;
  }

  // Unknown pattern
  return { ...result, page: null };
}

/**
 * SlugResolver — resolves a slug URL (`/baton-cafe/...`) into the correct QR page.
 * Handles all sub-paths: menu, table, category, item, cart.
 */
export const SlugResolver = () => {
  const params = useParams();
  const slug = params.slug;
  const rest = params['*'] || '';
  const { lang } = useLanguage();

  const parsed = React.useMemo(() => parseSlugPath(rest), [rest]);

  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/restaurants/by-slug/${encodeURIComponent(slug)}`
        );
        if (!cancelled) {
          setRestaurant(data);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setNotFound(true);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="slug-resolver-loading" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-600 text-sm">Yüklənir...</p>
        </div>
      </div>
    );
  }

  if (parsed.page === null || notFound || !restaurant) {
    return <Navigate to="/" replace />;
  }

  const { tableNumber, page, entityId } = parsed;
  const commonProps = {
    restaurantIdOverride: restaurant.id,
    slugOverride: slug,
    tableNumberOverride: tableNumber,
  };

  let content;
  if (page === 'category') {
    content = <QRCategory {...commonProps} categoryIdOverride={entityId} />;
  } else if (page === 'item') {
    content = <QRItem {...commonProps} itemIdOverride={entityId} />;
  } else {
    // 'menu' or 'cart' — both render QRMenu; cart is a drawer on top of it
    content = <QRMenu {...commonProps} />;
  }

  return (
    <CartProvider restaurantId={restaurant.id} tableNumber={tableNumber || null}>
      {content}
    </CartProvider>
  );
};
